<script setup lang="ts">
/**
 * PersonAvatar: the design system's people avatar (v2.1 "Wave set").
 *
 * Which picture shows, in order:
 *   1. `photo`, if one is set and it loads;
 *   2. `wave` (1-12), a saved choice, if given;
 *   3. the automatic wave from `userId` (FNV-1a, see utils/avatarWave.ts);
 *   4. the fallback, when there is no ID yet.
 *
 * People only. Companies and projects use a rounded square, not this circle.
 * Initials in a circle are retired: never render them here.
 */
import { computed, ref, watch } from 'vue';
import { avatarPx, avatarWave, waveFileStem, type AvatarSize } from '@/utils/avatarWave';

const waveFiles = import.meta.glob('../../assets/images/avatars/wave/*.svg', {
    eager: true,
    import: 'default',
}) as Record<string, string>;

function waveUrl(stem: string): string {
    return waveFiles[`../../assets/images/avatars/wave/${stem}.svg`] || '';
}

type Status = 'available' | 'away' | 'offline' | 'success' | 'warning' | 'neutral';

const props = withDefaults(
    defineProps<{
        userId?: string | number | null;
        name?: string | null;
        size?: AvatarSize | number | string;
        photo?: string | null;
        /** A saved wave choice (1-12); wins over the automatic one. */
        wave?: number | string | null;
        /** Optional presence dot: success / warning / neutral only. */
        status?: Status | null;
        /** Next to a visible name: true gives alt="" (the name is already read). */
        decorative?: boolean;
    }>(),
    { userId: null, name: '', size: 'md', photo: null, wave: null, status: null, decorative: false },
);

const photoFailed = ref(false);
watch(
    () => props.photo,
    () => {
        photoFailed.value = false;
    },
);

const px = computed(() => avatarPx(props.size));

const showPhoto = computed(() => {
    const p = typeof props.photo === 'string' ? props.photo.trim() : '';
    // The API serves its own placeholder when nobody uploaded a photo: that is
    // "no photo", and the wave should show instead.
    return !!p && !photoFailed.value && !p.includes('palceholder_avatar');
});

const waveSrc = computed(() => {
    const saved = Number(props.wave);
    if (Number.isInteger(saved) && saved >= 1 && saved <= 12) return waveUrl(waveFileStem(saved));
    const auto = avatarWave(props.userId);
    return waveUrl(auto ? waveFileStem(auto) : 'fallback');
});

const src = computed(() => (showPhoto.value ? (props.photo as string) : waveSrc.value));
const alt = computed(() => (props.decorative ? '' : props.name || ''));

const dotTone = computed(() => {
    switch (props.status) {
        case 'available':
        case 'success':
            return 'success';
        case 'away':
        case 'warning':
            return 'warning';
        case 'offline':
        case 'neutral':
            return 'neutral';
        default:
            return null;
    }
});
// Dot 10-14px, scaled to the avatar.
const dotPx = computed(() => Math.max(10, Math.min(14, Math.round(px.value * 0.28))));
</script>

<template>
    <span
        class="x-avi person-avatar"
        :class="{ 'person-avatar--photo': showPhoto }"
        :style="{ width: px + 'px', height: px + 'px' }"
        :title="decorative ? undefined : name || undefined"
    >
        <img
            class="person-avatar__img"
            :src="src"
            :alt="alt"
            :aria-hidden="decorative ? 'true' : undefined"
            :width="px"
            :height="px"
            draggable="false"
            @error="showPhoto && (photoFailed = true)"
        />
        <span
            v-if="dotTone"
            class="person-avatar__dot"
            :class="'person-avatar__dot--' + dotTone"
            :style="{ width: dotPx + 'px', height: dotPx + 'px' }"
            aria-hidden="true"
        ></span>
    </span>
</template>

<style scoped>
.person-avatar {
    position: relative;
    display: inline-block;
    flex: none;
    vertical-align: middle;
    border-radius: 50%;
    line-height: 0;
}

.person-avatar__img {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    user-select: none;
}

/* Photos sit in the same circle with a faint inset edge. */
.person-avatar--photo::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    box-shadow: inset 0 0 0 1px rgba(16, 24, 40, 0.08);
    pointer-events: none;
}

.person-avatar__dot {
    position: absolute;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    box-shadow: 0 0 0 2px var(--bs-surface, #fff);
}

.person-avatar__dot--success {
    background: var(--bs-success-500);
}

.person-avatar__dot--warning {
    background: var(--bs-warning-500);
}

.person-avatar__dot--neutral {
    background: var(--bs-text-muted);
}
</style>

<style>
/* Same gap as Vuetify gives a v-avatar in a list item's prepend slot. */
.v-list-item__prepend > .person-avatar ~ .v-list-item__spacer {
    width: 16px;
}

.v-list-item--slim .v-list-item__prepend > .person-avatar ~ .v-list-item__spacer {
    width: 4px;
}
</style>
